import { useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import tdistance from "@turf/distance";
import {
  LineString,
  Point,
  geometryCollection,
  point,
  polygon as tPolygon
} from "@turf/helpers";
import transformTranslate from "@turf/transform-translate";
import {
  WELL_LAYER,
  XDA_INTERCEPT_LAYER,
  XDA_LABEL
} from "constants/mapLayers.constants";
import { selectXdaInterceptData, selectXdaLineRequest, useMapStore } from "stores/map";
import {
  selectWellCardPanelOpen,
  selectWellCardPreviewOpen,
  useWellCardPanelStore
} from "stores/wellCardPanel";
import { Vector2 } from "three";

import { ToolbarButton } from "components/base";
import { useDashboardContext } from "components/dashboard/hooks/useDashboardContext";
import { useSelectedWellFromStore } from "components/data-analyzer/hooks";
import { useGeomBinContext } from "components/geom-bin/hooks/useGeomBinContext";
import Line from "components/icons/Line";

import { useMapContext } from "../hooks/useMapContext";
import { useMapDispatch } from "../hooks/useMapDispatch";
import { modes } from "../utils/drawConstants";
import { emptyFeatureCollection } from "../utils/emptyFeatureCollection";
import { getXdaLineNearCenterOfMap } from "../utils/getXdaLineNearCenterOfMap";
import { setXdaLineLabels } from "../utils/mapboxHelper";
import {
  getPerpendicularLineAndMakeXdaRequest,
  makeXdaLineRequest,
  resolveXdaWells
} from "../utils/xdaLineHelpers";

export interface XdaLineMarkerComponentModel {
  localState;
  selectionPolygonRef;
  getAndSetFeaturesIntersectingPolygon;
  drawRef;
}

export default function XdaLineMarker({
  localState,
  getAndSetFeaturesIntersectingPolygon,
  drawRef,
  selectionPolygonRef
}: XdaLineMarkerComponentModel) {
  const [isInVisDashboard, setIsInVisDashboard] = useState(false);
  const [defaultXdaLineDrawn, setDefaultXdaLineDrawn] = useState(false);
  // One default line per stretch of the dashboard having a vis widget. The
  // well card closing re-runs the effect that draws it, and a fresh
  // centre-of-map line there would replace the one the card just handed back.
  const defaultLineDrawnRef = useRef(false);
  const { mapbox, isMapInitialized } = useMapContext();
  const mapDispatch = useMapDispatch();
  const { isActive: isGeomBinOpen } = useGeomBinContext();
  const selectedWell = useSelectedWellFromStore();

  const selectedWellUwi = selectedWell?.Uwi;
  const debounceRef = useRef<Maybe<NodeJS.Timeout>>(null);
  const dashboardRef = useRef({
    isInSingleDashboard: false,
    isInVisDashboard
  });
  // While the well card panel is open the panel owns the shared line: both
  // derives write the same store entry, and last-write-wins would make the
  // panel's half-width intermittently no-op.
  const isWellCardPanelOpen = useWellCardPanelStore(selectWellCardPanelOpen);
  const isPreviewPopupOpen = useWellCardPanelStore(selectWellCardPreviewOpen);
  const drawLineRequestId = useWellCardPanelStore((s) => s.drawLineRequestId);
  const drawLineCancelId = useWellCardPanelStore((s) => s.drawLineCancelId);

  // Disable the XDA Escape handler while the geom-bin drawer is open. This tool
  // shares the MapboxDraw instance with GeomBinDrawer; clearXda() calls
  // deleteAll() on it, which would wipe every polygon the user drew in the
  // geom-bin drawer when they press Escape (EVA-6385).
  //
  // And while the well card panel is open, for the same reason its derive
  // stands down: the section on the map is the panel's, so Escape reaching in
  // here would empty the card's gunbarrel from underneath it. The same gate as
  // the derive, so the two cannot disagree about who owns the line.
  //
  // The card's map preview popup gates it too: that popup closes on Escape,
  // and the press that closes it is not asking for the section to go with it.
  useHotkeys(
    "esc",
    () => {
      if (!mapbox) {
        return;
      }
      clearXda();
    },
    { enabled: !isGeomBinOpen && !isWellCardPanelOpen && !isPreviewPopupOpen },
    [mapbox, isGeomBinOpen, isWellCardPanelOpen, isPreviewPopupOpen]
  );

  const { capabilities } = useDashboardContext();
  useEffect(() => {
    if (!capabilities) return;

    dashboardRef.current = {
      isInSingleDashboard: capabilities.hasSingleWellWidget,
      isInVisDashboard: capabilities.hasXdaWidget || capabilities.hasThreeDWidget
    };
    setIsInVisDashboard(dashboardRef.current.isInVisDashboard);
  }, [capabilities]);

  const xdaInterceptData = useMapStore(selectXdaInterceptData);
  const xdaLineRequest = useMapStore(selectXdaLineRequest);
  const dispatch = useDispatch();
  const setXdaLineRequest = useMapStore((s) => s.setXdaLineRequest);
  // The ref stays: map event handlers close over their creation render and read
  // it there. The state is what render reads. Without it nothing re-renders on
  // toggle, so the button does not light up and `aria-pressed` stays "false"
  // until some unrelated render happens to flush it.
  const isLineSelectRef = useRef(false);
  const [isLineSelect, setIsLineSelect] = useState(false);

  const setLineSelect = (value) => {
    // Turning the mode OFF still has to run with no draw control: the control
    // can go while a draw is armed, and the panel's button would otherwise
    // stay lit with nothing behind it.
    if (value && !drawRef.current) return;

    if (value) {
      drawRef.current.changeMode("two_point_line");
    } else {
      drawRef.current?.deleteAll();
      // Off means off for the map as well. Left in the draw mode it keeps the
      // crosshair and swallows the next click, so the first press on a well
      // does nothing and only the second one selects it.
      drawRef.current?.changeMode(modes.SIMPLE_SELECT);
    }

    isLineSelectRef.current = value;
    setIsLineSelect(value);
    // The panel's draw button is this same control, rendered in the card. It
    // lights up off the store, so the armed state has to reach it.
    useWellCardPanelStore.getState().setDrawArmed(value);
  };

  function clearXda() {
    clearXdaInterceptLine(mapbox);
    clearXdaLabel(mapbox);
    if (drawRef?.current) {
      drawRef.current.deleteAll();
    }
    mapDispatch({
      payload: {
        selectedFeatures: []
      }
    });
    mapDispatch({
      payload: {
        highlightedFeatures: { line: [], point: [], facility: [] }
      }
    });
  }

  /**
   * Draw the section line that was asked for. Shared by the two effects below:
   * one paints a fresh request, the other puts the stroke back when an answer
   * is withdrawn while the request still stands.
   *
   * No intercept points — only the /line response knows where the wells cross,
   * so a request can only ever draw the line itself.
   */
  function paintLineFromRequest(requested) {
    const source = mapbox?.getSource(XDA_INTERCEPT_LAYER) as mapboxgl.GeoJSONSource;
    if (!source) {
      return;
    }
    const line = {
      type: "LineString",
      coordinates: [
        [requested[0][0], requested[0][1]],
        [requested[1][0], requested[1][1]]
      ]
    } as LineString;
    const gcoll = geometryCollection([line]);
    // The drag handler translates whatever geometry is recorded here, so a
    // line painted from the request has to be the one a drag moves.
    localState.current.xdaInterceptLine = gcoll;
    source.setData(gcoll);
    setXdaLineLabels(mapbox, {
      start: { x: requested[0][0], y: requested[0][1] },
      end: { x: requested[1][0], y: requested[1][1] }
    });
  }

  function clearXdaInterceptLine(mapbox) {
    if (!mapbox) {
      return;
    }
    const xda = mapbox.getSource(XDA_INTERCEPT_LAYER);
    if (!xda) {
      return;
    }
    xda.setData(emptyFeatureCollection);
  }

  function clearXdaLabel(mapbox) {
    if (!mapbox) {
      return;
    }
    const label = mapbox.getSource(XDA_LABEL);
    if (!label) {
      return;
    }
    label.setData(emptyFeatureCollection);
  }

  // The panel's draw button sits outside the map, so it asks for a draw
  // through the store rather than reaching into this component's draw ref.
  useEffect(() => {
    if (!drawLineRequestId) {
      return;
    }
    if (!drawRef?.current) {
      // The draw control has not attached yet. Say so, rather than leaving the
      // panel's button looking like it did nothing.
      toast.warn("The map is still loading. Try the draw tool again in a moment.");
      return;
    }
    setLineSelect(true);
  }, [drawLineRequestId]);

  // Pressing the panel's lit button while a draw is armed, or closing the card
  // then, asks for the pending two clicks to be given up.
  useEffect(() => {
    if (!drawLineCancelId) {
      return;
    }
    setLineSelect(false);
  }, [drawLineCancelId]);

  useEffect(() => {
    if (localState.current.xdaLineMoved) {
      // This selected well uwi was changed following a line move event
      // since the xda line was already triggered by the line move event
      // skip it here
      localState.current.xdaLineMoved = false;
      return;
    }

    if (defaultXdaLineDrawn) {
      // We don't want to draw a new line if the default line has already been drawn
      setDefaultXdaLineDrawn(false);
      return;
    }

    if (
      !selectedWellUwi ||
      !mapbox ||
      isLineSelectRef.current ||
      isWellCardPanelOpen ||
      (!dashboardRef.current.isInSingleDashboard &&
        !dashboardRef.current.isInVisDashboard)
    ) {
      //only register click if dashboard has vis or single well
      return;
    }
    if (!mapbox.getLayer(WELL_LAYER)) {
      return;
    }

    const selectedWellIds = {};
    selectedWellIds[selectedWellUwi] = true;

    // get feature of selected well
    const features = mapbox.querySourceFeatures(WELL_LAYER, {
      sourceLayer: "eva-wells",
      filter: ["has", ["get", "Uwi"], ["literal", selectedWellIds]]
    });

    if (features.length === 0 || !features[0]) {
      return;
    }

    const feat = features[0];
    if (!feat) {
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      getPerpendicularLineAndMakeXdaRequest(
        selectedWellUwi,
        // We need to pass in the selected feature, otherwise the line will be drawn for all nearby wells
        feat,
        mapbox,
        dispatch,
        selectionPolygonRef,
        getAndSetFeaturesIntersectingPolygon
      );
    }, 100); // 100 ms debounce
  }, [selectedWellUwi, isWellCardPanelOpen]);

  useEffect(() => {
    // An edge case, this prevents the xda line from snapping to it's default position while moving,
    // when xdaInterceptData is changed, this can happen when user zooms in/out, or moves the map
    if (localState.current.isXdaLineMoving) {
      return;
    }

    if (!mapbox || !xdaInterceptData || xdaInterceptData.line.length === 0) {
      // An answer going away is not the same as the line going away. A viewer
      // unmounting, a style reload, or a clear that lands between two answers
      // all empty this while the request still stands — and nothing repaints
      // afterwards, because the request has not changed. So the stroke is
      // drawn back from the request rather than left off the map.
      //
      // Read from the store, not a dependency: this effect answers to the
      // intercept data, and depending on the request would re-run it for
      // changes it has nothing to say about.
      const stillRequested = useMapStore.getState().xdaLineRequest?.line;
      if (stillRequested?.length >= 2) {
        paintLineFromRequest(stillRequested);
        return;
      }
      // Nothing is asked for either, so this is the real teardown — the one
      // `clearSharedLine` performs, which empties the request as well. The
      // stroke and its labels both go, as `clearXda` does; clearing only the
      // labels left an unexplained stroke across the map.
      clearXdaInterceptLine(mapbox);
      clearXdaLabel(mapbox);
      return;
    }

    const line = {
      type: "LineString",
      coordinates: [
        [xdaInterceptData.line[0][0], xdaInterceptData.line[0][1]],
        [xdaInterceptData.line[1][0], xdaInterceptData.line[1][1]]
      ]
    } as LineString;

    const geoms = xdaInterceptData.points.map((p) => {
      return { type: "Point", coordinates: [p[0], p[1]] };
    }) as Point[];

    const gcoll = geometryCollection(geoms);
    gcoll.geometry.geometries.push(line);
    const source = mapbox.getSource(XDA_INTERCEPT_LAYER) as mapboxgl.GeoJSONSource;
    if (!source) {
      return;
    }
    localState.current.xdaInterceptLine = gcoll;
    source.setData(gcoll);
    // This is the only path that paints the card's derived line - it never
    // goes through `setXdaLine` - so the labels are written here too, by the
    // same helper and so with the same A / A' orientation.
    //
    // Labelled from the line that was asked for, which is the array the viewer
    // reads to decide which end it calls A. A response that came back with its
    // two ends the other way round would otherwise put the map's A on the
    // opposite end of the section from the viewer's.
    const requested = useMapStore.getState().xdaLineRequest?.line;
    const labelled = requested?.length >= 2 ? requested : xdaInterceptData.line;
    setXdaLineLabels(mapbox, {
      start: { x: labelled[0][0], y: labelled[0][1] },
      end: { x: labelled[1][0], y: labelled[1][1] }
    });
  }, [xdaInterceptData]);

  useEffect(() => {
    function onMouseMove(e) {
      const coords = e.lngLat;
      const prevMouseCoordinate = localState.current.currentMouseCoordinate;

      if (localState.current.xdaInterceptLine) {
        const from = point([prevMouseCoordinate.lng, prevMouseCoordinate.lat]);
        const to = point([coords.lng, coords.lat]);

        //angle from north in axis degree
        let angle =
          new Vector2(
            coords.lng - prevMouseCoordinate.lng,
            coords.lat - prevMouseCoordinate.lat
          ).angle() *
          (180 / Math.PI);
        angle = 360 - (angle - 90);
        const distance = tdistance(from, to, { units: "kilometers" });
        transformTranslate(localState.current.xdaInterceptLine, distance, angle, {
          mutate: true
        });
        const source = mapbox.getSource(XDA_INTERCEPT_LAYER) as mapboxgl.GeoJSONSource;
        source.setData(localState.current.xdaInterceptLine);
        localState.current.currentMouseCoordinate = coords;
      }
    }

    async function onDrawEnd(e) {
      //calls when finished drawing
      if (e.features.length > 0) {
        const feature = e.features[0];

        const geom = feature.geometry;
        if (geom.type !== "LineString") {
          return;
        }
        //convert linestring to polygon
        await makeXdaLineRequest(
          geom,
          {},
          mapbox,
          dispatch,
          selectionPolygonRef,
          getAndSetFeaturesIntersectingPolygon
        );
        if (drawRef?.current) {
          drawRef.current.deleteAll();
        }
        // Read now, not captured: this handler closes over the render that
        // registered it, so any state it took from that render is stale.
        const panel = useWellCardPanelStore.getState();
        if (panel.open) {
          panel.setCustomLineActive(true);
        }
        if (panel.open && panel.drawArmed) {
          // The card's button is still lit, so the user is still drawing: this
          // line replaces the last one and the next two clicks draw the next.
          // The mode stands itself down after the second point, so re-entering
          // it is what keeps the tool live. Standing the button down is left to
          // the user pressing it again, or to the card closing.
          drawRef?.current?.changeMode("two_point_line");
          return;
        }
        // A draw from the map's own toolbar is one shot.
        setLineSelect(false);
      }
    }

    if (!mapbox) {
      return;
    }

    function onMouseUp() {
      localState.current.isXdaLineMoving = false;

      //get centroid of line and pass it to xda viewer
      const geom = localState.current.xdaInterceptLine;
      if (!geom || !geom.geometry || !geom.geometry.geometries) {
        return;
      }
      const line = geom.geometry.geometries.filter((x) => x.type === "LineString");
      if (!line) {
        return;
      }

      mapbox.off("mousemove", onMouseMove);
      mapbox.off("mouseup", onMouseUp);
      if (localState.current.xdaLineMoved) {
        // A drag rewrites the same shared line a drawn one does, so the panel
        // has to be told it no longer owns it — otherwise closing the card
        // wipes the dragged line, and a half-width step re-derives over it.
        // Flagged before the request below, not after: the 500 ms wait is
        // exactly the window a re-derive would land in.
        const panel = useWellCardPanelStore.getState();
        if (panel.open) {
          panel.setCustomLineActive(true);
        }
        setTimeout(
          async () =>
            await makeXdaLineRequest(
              line[0],
              {},
              mapbox,
              dispatch,
              selectionPolygonRef,
              getAndSetFeaturesIntersectingPolygon
            ),
          500
        );
      }
    }

    // eslint-disable-next-line listeners/no-missing-remove-event-listener -- Mapbox GL .on()/.off() API handles its own listener lifecycle. The mousedown handler is layer-specific and cleaned up in the useEffect return below.
    mapbox.on("mousedown", XDA_INTERCEPT_LAYER, (e) => {
      localState.current.isXdaLineMoving = true;

      e.preventDefault();

      const state = localState.current;
      if (!state) {
        return;
      }
      const coords = e.lngLat;
      state.currentMouseCoordinate = coords;
      state.xdaLineMoved = true;

      mapbox.on("mousemove", onMouseMove);
      mapbox.on("mouseup", onMouseUp);
    });

    // eslint-disable-next-line listeners/no-missing-remove-event-listener -- Mapbox GL .on()/.off() API handles its own listener lifecycle. Cleanup is in the useEffect return below.
    mapbox.on("draw.create", onDrawEnd);

    return () => {
      mapbox.off("mousedown", XDA_INTERCEPT_LAYER);
      mapbox.off("mousemove", onMouseMove);
      mapbox.off("mouseup", onMouseUp);
      mapbox.off("draw.create", onDrawEnd);
      drawRef.current = undefined;
    };
  }, [drawRef, localState, mapbox]);

  async function setDefaultXdaLine() {
    //is vis dash so we want to draw xda line at random well near center of screen
    if (!mapbox) {
      return;
    }

    const { line } = await getXdaLineNearCenterOfMap(mapbox);

    if (!line) {
      return;
    }

    // send notification on the default xda line was being drawn
    setDefaultXdaLineDrawn(true);

    const poly = tPolygon([
      [
        [line.start.x, line.start.y],
        [line.end.x, line.end.y],
        [line.end.x + 0.0001, line.end.y + 0.0001],
        [line.start.x, line.start.y]
      ]
    ]);
    const [selectedWells, wellsIntersect] = await getAndSetFeaturesIntersectingPolygon(
      poly
    );
    setXdaLineRequest({
      line: [
        [line.start.x, line.start.y],
        [line.end.x, line.end.y]
      ],
      wells: resolveXdaWells(selectedWells, wellsIntersect)
    });
  }

  useEffect(() => {
    async function setInitialXda() {
      let count = 0;
      while ((!mapbox || !mapbox.areTilesLoaded) && count < 5) {
        await new Promise((r) => setTimeout(r, 1000));
        count++;
      }
      setDefaultXdaLine();
    }

    // The card owns the shared line while it is open - the same gate the
    // click derive above uses, so the two cannot disagree about who owns it.
    // The flag is a dependency as well as a guard, or this would never stand
    // back up: nothing else in the list changes when the card closes.
    if (isWellCardPanelOpen) {
      return;
    }

    if (isInVisDashboard && isMapInitialized) {
      if (!dashboardRef.current.isInSingleDashboard && !defaultLineDrawnRef.current) {
        // stop drawing default xda line if in single dashboard
        defaultLineDrawnRef.current = true;
        setInitialXda();
      }
    } else {
      defaultLineDrawnRef.current = false;
      clearXda();
    }
  }, [isInVisDashboard, dashboardRef.current, isMapInitialized, isWellCardPanelOpen]);

  /*
   * Paint the line that was ASKED for, rather than waiting for a viewer to
   * answer it. The stroke came only from `xdaInterceptData`, and nothing but a
   * mounted XDA viewer writes that: with the card's gunbarrel showing the 3D
   * view there is no XDA viewer, so retargeting the card left the previous
   * well's section - and its A / A' - lying across the map on the wrong well.
   *
   * Last of this component's effects on purpose. Effects run in declaration
   * order within a commit, and the clear above fires on the first render after
   * mount when the dashboard has no vis widget; painting from here means that
   * clear cannot land on top of the line it is about to draw.
   *
   * The orange intercept points cannot be drawn from a request - only the
   * /line response knows where the wells cross - so an answer that already
   * describes this line is left alone rather than repainted without its
   * points. Read from the store instead of a dependency: this effect is about
   * the request, and re-running it when an answer lands would only re-check
   * the same guard.
   *
   * Clearing stays with the intercept effect's empty branch: a retarget empties
   * the request before the new line lands, and tearing the stroke down here
   * would blink the section off and on between two wells.
   *
   * `isMapInitialized` is a dependency because the first thing this does is
   * read the intercept source, and that source does not exist until the style
   * has been built - nor during a basemap switch, which tears the sources down
   * and adds them again. In either window this would return with nothing
   * painted, and the request has not changed, so nothing would run it again.
   */
  useEffect(() => {
    if (!mapbox || localState.current.isXdaLineMoving) {
      return;
    }
    const requested = xdaLineRequest?.line;
    if (!requested || requested.length < 2) {
      return;
    }
    const answered = useMapStore.getState().xdaInterceptData?.line;
    if (
      answered?.length >= 2 &&
      answered[0][0] === requested[0][0] &&
      answered[0][1] === requested[0][1] &&
      answered[1][0] === requested[1][0] &&
      answered[1][1] === requested[1][1]
    ) {
      return;
    }
    paintLineFromRequest(requested);
  }, [xdaLineRequest, mapbox, isMapInitialized]);

  if (!isInVisDashboard || !mapbox || isGeomBinOpen) {
    return <></>;
  }
  return (
    <ToolbarButton
      active={isLineSelect}
      disabled={drawRef?.current === undefined}
      icon={<Line />}
      onToggle={setLineSelect}
      // Named for the guided walkthrough, which spotlights this as the way
      // into a cross-section.
      data-testid="xda-line-toggle"
      tooltipText="Toggle Line Selection"
      styleKey="mapToolbar"
    />
  );
}
